import { Standing } from "../../../models/Standing";
import { StandingsPrediction } from "../../../models/StandingsPrediction";
import { TeamPrediction } from "../../../models/TeamPrediction";
import { getDivisionDisplayName } from "../../../utils/divisionUtils";
import { getStandingZone, StandingZone, ZONE_COLORS, ZONE_LABEL_KEYS } from "../../../utils/standingZones";
import styles from "../Predict.module.css";

/** Predictions start in season 28 (season 27 holds a single test row). */
export const FIRST_PREDICTION_SEASON = 28;

export type StandingsByDivision = Record<string, Standing[]>;
export type PredictionsByDivision = Record<string, StandingsPrediction>;
/** A division's ranking being built: slot i holds the team predicted at rank i + 1, or null. */
export type Slots = (string | null)[];

const MAJOR_ORDER = ["ligaen", "1div", "2div", "3div", "4div", "5div"];

export const majorOf = (division: string): string =>
  division === "ligaen" ? "ligaen" : division.replace(/[A-D]$/, "");

export const groupOf = (division: string): string =>
  division !== "ligaen" && /[A-D]$/.test(division) ? division.slice(-1) : "";

const orderKey = (division: string): number => {
  const major = MAJOR_ORDER.indexOf(majorOf(division));
  const group = groupOf(division);
  return (major < 0 ? 99 : major) * 10 + (group ? group.charCodeAt(0) - 64 : 0);
};

export const compareDivisions = (a: string, b: string): number => orderKey(a) - orderKey(b);

/** The divisions you can predict: those with teams in the standings, in league order. */
export const predictableDivisions = (standings: StandingsByDivision | null): string[] =>
  standings
    ? Object.keys(standings).filter((d) => (standings[d]?.length ?? 0) > 0).sort(compareDivisions)
    : [];

/** "Ligaen", "1. Division", "2. Division A". */
export const divisionLabel = (division: string): string =>
  getDivisionDisplayName(division).replace(/^(\d) /, "$1. ");

/** More new faces than this since last season flags the team's last-season placing as less telling. */
export const NEW_PLAYER_THRESHOLD = 2;

/** Pill label for a major division: "Ligaen", "1. div". */
export const majorLabel = (major: string): string =>
  major === "ligaen" ? "Ligaen" : `${major.replace("div", "")}. div`;

/** Groups divisions by major, keeping league order: [["ligaen"], ["1div"], ["2divA","2divB"], ...]. */
export const majorsOf = (divisions: string[]): { major: string; divisions: string[] }[] => {
  const out: { major: string; divisions: string[] }[] = [];
  for (const division of divisions) {
    const major = majorOf(division);
    const last = out[out.length - 1];
    if (last && last.major === major) last.divisions.push(division);
    else out.push({ major, divisions: [division] });
  }
  return out;
};

/** Only predictions with at least one team count as saved (the old page could save an empty one). */
export const toPredictionMap = (predictions: StandingsPrediction[]): PredictionsByDivision => {
  const map: PredictionsByDivision = {};
  for (const p of predictions) {
    if (p.teams?.length) map[p.division] = p;
  }
  return map;
};

/**
 * Lays a saved prediction onto the division's current teams: one slot per team, a saved team that
 * left the division is dropped, and a team that joined it leaves its slot empty.
 */
export const slotsFromTeams = (teams: TeamPrediction[] | undefined, standings: Standing[]): Slots => {
  const slots: Slots = standings.map(() => null);
  if (!teams) return slots;
  const inDivision = new Set(standings.map((s) => s.team.teamId));
  for (const t of teams) {
    const index = t.predictedRank - 1;
    if (index >= 0 && index < slots.length && inDivision.has(t.teamId) && !slots.includes(t.teamId)) {
      slots[index] = t.teamId;
    }
  }
  return slots;
};

/** Keeps an in-progress ranking valid against the division's current teams (same size, known ids). */
export const sanitizeSlots = (slots: Slots, standings: Standing[]): Slots => {
  const inDivision = new Set(standings.map((s) => s.team.teamId));
  const seen = new Set<string>();
  return standings.map((_, i) => {
    const id = slots[i] ?? null;
    if (!id || !inDivision.has(id) || seen.has(id)) return null;
    seen.add(id);
    return id;
  });
};

export const sameSlots = (a: Slots, b: Slots): boolean =>
  a.length === b.length && a.every((id, i) => id === b[i]);

export const zoneColor = (season: number, division: string, index: number): string =>
  ZONE_COLORS[getStandingZone(season, division, index)];

/** The zones a division's table uses, in order, with their i18n label key. */
export const legendFor = (season: number, division: string, size: number): { zone: StandingZone; labelKey: string; color: string }[] => {
  const seen: StandingZone[] = [];
  for (let i = 0; i < size; i++) {
    const zone = getStandingZone(season, division, i);
    if (zone !== "white" && !seen.includes(zone)) seen.push(zone);
  }
  return seen.map((zone) => ({
    zone,
    // Ligaen's green is the LAN spots from season 33, not a promotion.
    labelKey: division === "ligaen" && zone === "green" ? "predict.zoneLan" : ZONE_LABEL_KEYS[zone as Exclude<StandingZone, "white">],
    color: ZONE_COLORS[zone],
  }));
};

export const deviationClass = (deviation: number): string =>
  deviation === 0 ? styles.devExact : deviation <= 2 ? styles.devClose : styles.devFar;

/** "12. sep." — date only: the server stores a naive timestamp, so an hour could be off by the TZ offset. */
export const formatSavedDate = (value: string | Date | undefined, locale: string): string => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(locale === "en" ? "en-GB" : "da-DK", { day: "numeric", month: "short" });
};
